// useAiEnabled — single source of truth for "can the AI subsystem run
// at all?". Composes three signals:
//
//   1. Runtime kill-switch (admin → System → AI off)
//   2. Tier capability (limits.canUseAI)
//   3. Auth state (anon can't burn tokens)
//
// Use this in any FE call-site that's about to invoke an AI feature.
// Edge functions enforce the same gate server-side via
// assertSystemEnabled('ai') in Fas 6's _shared/systemSwitches.ts.

import { useMemo } from 'react';
import { usePlan } from '@/hooks/usePlan';
import { useAuth } from '@/contexts/AuthContext';
import { useSystemSwitches } from '@/hooks/useSystemSwitches';

export interface AiEnabledState {
  /** True only when all three gates pass. */
  enabled: boolean;
  /** True when the admin has flipped the system kill-switch off. */
  systemDisabledByAdmin: boolean;
  /** True when the caller's tier blocks AI features. */
  blockedByTier: boolean;
  /** True when the caller isn't authenticated. */
  blockedByAuth: boolean;
}

export function useAiEnabled(): AiEnabledState {
  const { isAuthenticated } = useAuth();
  const { limits } = usePlan();
  const { switches } = useSystemSwitches();

  return useMemo(() => {
    const systemDisabledByAdmin = !switches.ai;
    const blockedByTier = !limits.canUseAI;
    const blockedByAuth = !isAuthenticated;
    return {
      enabled: !systemDisabledByAdmin && !blockedByTier && !blockedByAuth,
      systemDisabledByAdmin,
      blockedByTier,
      blockedByAuth,
    };
  }, [isAuthenticated, limits.canUseAI, switches.ai]);
}
